import React, { Component } from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import lscache from 'lscache'
import humanizeList from 'humanize-list'

import { showBanner } from '../../redux/banners'
import { saveAlbum, deleteAlbum } from '../../redux/album'
import { unshareAlbum } from '../../redux/share'
import { emptyStructure, excludeImages, enableImageReorder, fixAlbum, unfixAlbum } from '../../redux/query'
import { fetchPlaylists, saveList, addAlbumToList, removeAlbumFromList } from '../../redux/playlists'
import {
  openModal,
  closeModal,
  SAVE_ALBUM_MODAL_KEY
} from '../../redux/modals'

import SaveAlbum from '../modals/SaveAlbum'
import DownloadImages from '../modals/DownloadImages'
import AddImagesToAlbum from './AddImagesToAlbum'
import ImageShare from './ImageShare'
import PresentMenu from '../present/PresentMenu'
import DropMenu from '../DropMenu'
import LoadingSpinner from '../LoadingSpinner'
import ChannelMenuItem from '../present/ChannelMenuItem'
import { picassoFetch } from '../../utils/fetch'
import { pluralize } from '../../utils/string'
import { getHash } from '../../utils/images'
import { userHasPerm } from '../../utils/auth'
import { updateAlbumLists } from '../../utils/lists'
import {
  QUERY_CACHE_KEY,
  MAX_FIXED_COUNT,
  FIX_WARNING,
  UNFIX_WARNING
} from '../../constants'

class AlbumMenu extends Component {
  state = {
    presentMenuOpen: false,
    downloadMenuOpen: false,
    shareImagesMenuOpen: false,
    downloadSizeSelected: null,
    playNowMenuOpen: false,
    playNextMenuOpen: false,
    addImagesMenuOpen: false,
    addToChannel: null,
    playNow: false,
    tempCover: null
  }
  componentDidMount () {
    const { lists, fetchPlaylists } = this.props
    if (!lists) fetchPlaylists()
  }
  onSave = data => {
    const { lists, addAlbumToList, removeAlbumFromList, saveList } = this.props
    const albumData = {
      ...data,
      lists: undefined,
      createList: undefined
    }
    return this.saveAlbum(albumData).then(album => {
      return updateAlbumLists({
        albumID: album.id,
        checked: data.lists,
        createList: data.createList,
        lists,
        addAlbumToList,
        removeAlbumFromList,
        saveList
      })
    }).then(() => {
      lscache.remove(QUERY_CACHE_KEY)
    }).catch(e => console.log(e))
  }
  saveAlbum = data => {
    const { addingToPlaylist, query, saveAlbum } = this.props
    if (query.selectedImages.length !== 0) {
      return saveAlbum(data, {
        ...emptyStructure,
        include: query.selectedImages
      })
    }
    if (data.id) {
      return saveAlbum(data, query.structure, data.id)
    }
    if (addingToPlaylist) {
      const redirect = `/playlists/${addingToPlaylist.id}?addAlbum=:id`
      return saveAlbum(data, query.structure, undefined, redirect)
    }
    return saveAlbum(data, query.structure)
  }
  onDelete = () => {
    const { me, album, deleteAlbum, saveAlbum } = this.props
    if (album.user_id !== me.id) {
      // Unshare album from myself
      this.props.unshareAlbum(album.id, me.id)
      this.props.goHome()
    } else if (album.favourite || album.image_share) {
      // Empty special albums instead of deleting them
      saveAlbum({ name: album.name, cover: '' }, { ...emptyStructure, queries: [] }, album.id, '/')
    } else {
      deleteAlbum(album.id)
    }
  }
  onAddToChannel = (channel, options) => {
    const { sendToPlayNow, sendToPlayNext, showBanner, album, editing } = this.props
    const { playNow } = this.state
    const picassoStructure = this.generatePicassoStructure(options || {})
    const success = data => {
      if (!channel.id && playNow) {
        this.props.presentChannel(data.channel_id)
      } else {
        showBanner(album && !editing ? 'Album added to channel' : 'Images added to channel')
      }
    }
    if (playNow || !channel.id) {
      sendToPlayNow(channel.id, picassoStructure).then(success)
    } else {
      sendToPlayNext(channel.id, picassoStructure).then(success)
    }
  }
  generatePicassoStructure (presentOptions) {
    const { album, editing, images, query } = this.props
    const { selectedImages } = query
    const present = {
      presentationType: presentOptions.presentation_type,
      presentationSpeed: presentOptions.presentation_speed,
      presentationTransition: presentOptions.presentation_transition,
      presentationSort: presentOptions.presentation_sort
    }
    if (album && !editing) {
      return {
        album_id: album.id,
        album: {
          album: album.id,
          ...present
        }
      }
    }
    return {
      images: selectedImages.length !== 0 ? selectedImages : images.map(image => image._id),
      ...present
    }
  }
  getPresentMenuItems () {
    const { canEditPlaylists } = this.props
    const { playNow, channels } = this.state
    if (canEditPlaylists && !channels) {
      return [{ text: <LoadingSpinner style={{ width: '12px', height: '12px', margin: 0 }} /> }]
    }
    const items = []
    items.push({
      text: playNow ? 'Present on this device' : 'New channel (auto-generated)',
      // Use empty object for channel so that id is undefined and picasso creates one on the fly
      onClick: () => this.onAddToChannel({}, this.props.album),
      extra: [{
        icon: 'settings',
        onClick: () => this.setState({ presentMenuOpen: true, addToChannel: {} })
      }]
    })
    if (!canEditPlaylists) {
      return items
    }
    if (channels.length !== 0) {
      items.push({
        heading: 'Add to channel'
      })
    }
    return items.concat(channels.map(channel => ({
      key: channel.id,
      text: <ChannelMenuItem channel={channel} />,
      onClick: () => this.onAddToChannel(channel, this.props.album),
      extra: [{
        icon: 'settings',
        onClick: () => this.setState({ presentMenuOpen: true, addToChannel: channel })
      }]
    })))
  }
  getCover (album, images, selectedImages) {
    if (selectedImages.length !== 0) {
      return selectedImages[0]
    }
    if (album && album.cover) {
      return album.cover
    }
    return this.state.tempCover || (images && images[0] && images[0]._id)
  }
  enableImageReorder = () => {
    const { query, enableImageReorder, shouldSearchImages, searchImages } = this.props
    enableImageReorder()
    // Preload manual search structure to prevent another spinner when reordering
    const structure = {
      ...query.structure,
      sort: {
        ...query.structure.sort,
        value: 'manual',
        operator: null
      }
    }
    if (shouldSearchImages(structure)) {
      searchImages(structure)
    }
  }
  toggleLock = e => {
    e.stopPropagation()
    const { query, images, count, loadMoreImages, fixAlbum, unfixAlbum } = this.props
    if (query.structure.fixed) {
      if (window.confirm(UNFIX_WARNING) === false) {
        return
      }
      unfixAlbum()
    } else {
      if (window.confirm(FIX_WARNING) === false) {
        return
      }
      if (images.length !== count) {
        this.setState({ loadingAll: true })
        loadMoreImages(count).then(() => {
          fixAlbum(this.props.images.map(image => image._id))
          this.setState({ loadingAll: false })
        })
      } else {
        fixAlbum(images.map(image => image._id))
      }
    }
  }
  getLockText () {
    const { query } = this.props
    const { loadingAll } = this.state
    if (loadingAll) {
      return <LoadingSpinner style={{ width: '12px', height: '12px', margin: 0 }} />
    }
    return query.structure.fixed ? 'Unlock album' : 'Lock album'
  }
  render () {
    const {
      album, query, modals, images, count, loadMoreImages, canSave, canShare, canDelete, anonymousUser,
      openModal, closeModal, excludeImages, setCover, getImageSource, fetchChannels, addingToPlaylist
    } = this.props
    const { selectedImages, selectImageMode } = query
    const { presentMenuOpen, downloadMenuOpen, downloadSizeSelected, playNowMenuOpen, playNextMenuOpen, addToChannel, shareImagesMenuOpen, addImagesMenuOpen } = this.state

    const imagesAvailable = !!images && images.length !== 0 && (!selectImageMode || selectedImages.length !== 0)

    const canShareImages = this.props.canShareImagesInternal || this.props.canShareImagesExternal

    const mainMenuItems = [
      {
        text: this.getLockText(),
        onClick: this.toggleLock,
        shouldShow: this.props.canLock && count,
        disabled: count > MAX_FIXED_COUNT,
        disabledMessage: `Album must be less that ${MAX_FIXED_COUNT} images to be locked`
      },
      {
        text: 'Save as new album',
        onClick: () => this.props.saveAlbum({
          name: this.props.title,
          cover: images[0]._id
        }, query.structure),
        shouldShow: !!this.props.location.query.path
      },
      {
        text: 'Play now',
        onClick: () => {
          if (this.props.canEditPlaylists) {
            this.setState({
              channels: null,
              playNowMenuOpen: true,
              playNow: true
            })
            fetchChannels().then(channels => this.setState({ channels }))
          } else {
            this.setState({ presentMenuOpen: true })
          }
        },
        shouldShow: imagesAvailable
      },
      {
        text: 'Play next',
        onClick: () => {
          this.setState({
            channels: null,
            playNextMenuOpen: true,
            playNow: false
          })
          fetchChannels().then(channels => this.setState({ channels }))
        },
        shouldShow: imagesAvailable && !anonymousUser && this.props.canEditPlaylists
      },
      {
        text: 'Update Album Name/Settings',
        onClick: () => openModal(SAVE_ALBUM_MODAL_KEY),
        shouldShow: album && selectedImages.length === 0 && this.props.isOwnAlbum && this.props.canEdit
      },
      {
        text: `Add ${pluralize(selectedImages.length, 'image')} to album`,
        onClick: () => this.setState({ addImagesMenuOpen: true }),
        shouldShow: selectedImages.length !== 0
      },
      {
        text: 'Add to groups',
        href: album && `/album/${album.id}/groups`,
        shouldShow: album && !anonymousUser && selectedImages.length === 0
      },
      {
        text: 'Share album',
        href: album && `/album/${album.id}/share`,
        shouldShow: album && this.props.showShare && selectedImages.length === 0,
        disabled: !canShare,
        disabledMessage: 'You do not have permission to share this album'
      },
      {
        text: `Share ${pluralize(selectedImages.length, 'image')}`,
        onClick: () => this.setState({ shareImagesMenuOpen: true }),
        shouldShow: canShareImages && selectedImages.length !== 0
      },
      {
        text: selectedImages.length !== 0 ? `Download ${pluralize(selectedImages.length, 'image')}` : (album ? 'Download album' : 'Download all images'),
        onClick: e => {
          if (this.props.restrictDownload) {
            if (selectedImages.length >= 20 || (selectedImages.length === 0 && count >= 20)) {
              if (window.confirm('You are downloading a large number of images, which may take a long time on slow connections. Are you sure you want to continue?')) {
                this.setState({ downloadMenuOpen: true })
              }
              return
            }
          }
          this.setState({ downloadMenuOpen: true })
        },
        shouldShow: imagesAvailable && this.props.canDownload,
        disabled: this.props.restrictDownload && (selectedImages.length > 100 || (selectedImages.length === 0 && count > 100)),
        disabledMessage: 'You cannot download more than 100 images at once'
      },
      {
        text: `Remove ${pluralize(selectedImages.length, 'image')} from album`,
        onClick: () => excludeImages(selectedImages),
        shouldShow: this.props.canRemove && selectedImages.length !== 0
      },
      {
        text: 'Delete album',
        onClick: () => {
          let message = `Are you sure you want to delete ${album.name}?`
          const lists = this.props.albumLists.map(id => this.props.entities.playlists[id].name)
          if (lists.length !== 0) {
            message += ` It is part of ${humanizeList(lists)}`
          }
          if (window.confirm(message)) {
            this.onDelete()
          }
        },
        disabled: !canDelete,
        disabledMessage: 'You do not have permission to delete this album'
      },
      {
        text: 'Select all',
        onClick: () => this.props.selectImage(images.map(i => i._id)),
        shouldShow: selectImageMode && images.length !== selectedImages.length
      },
      {
        text: 'Select none',
        onClick: this.props.disableImageSelect,
        shouldShow: anonymousUser
      },
      {
        text: 'Set image as album cover',
        onClick: () => {
          if (album) {
            setCover(selectedImages[0])
          } else {
            this.setState({ tempCover: selectedImages[0] })
          }
        },
        shouldShow: this.props.canEdit && selectedImages.length === 1
      }
    ]

    const prefill = album && selectedImages.length === 0
    const initialFormValues = {
      id: prefill ? album.id : undefined,
      name: prefill ? album.name : '',
      cover: this.getCover(album, images, selectedImages),
      presentation_type: prefill && album.presentation_type || null,
      presentation_speed: prefill && album.presentation_speed || null,
      presentation_transition: prefill && album.presentation_transition || null,
      presentation_sort: prefill && album.presentation_sort || null
    }

    const presentMenuItems = this.getPresentMenuItems()

    return (
      <span>
        <DropMenu
          open={this.props.open}
          closeMenu={this.props.closeMenu}
          fixed={this.props.fixed}
          items={mainMenuItems}
          toggle='album-menu'
        />
        {presentMenuOpen &&
          <PresentMenu
            closeMenu={() => this.setState({ presentMenuOpen: false, addToChannel: null })}
            images={selectedImages.length !== 0 ? selectedImages : (images || []).map(image => image._id)}
            selectedImages={selectedImages}
            count={selectedImages.length || count}
            loadMoreImages={loadMoreImages}
            getImageSource={getImageSource}
            presentTypeSelected={this.props.presentTypeSelected}
            addToChannel={addToChannel}
            onAddToChannel={this.onAddToChannel}
            album={album}
            entities={this.props.entities}
          />
        }
        <DropMenu
          open={downloadMenuOpen}
          closeMenu={() => this.setState({ downloadMenuOpen: false })}
          fixed={this.props.fixed}
          items={[
            { text: 'Small', onClick: () => this.setState({ downloadSizeSelected: 's75' }) },
            { text: 'Medium', onClick: () => this.setState({ downloadSizeSelected: 'm75' }) },
            { text: 'Large', onClick: () => this.setState({ downloadSizeSelected: 'l75' }) },
            { text: 'Original', onClick: () => this.setState({ downloadSizeSelected: 'o' }) }
          ]}
        />
        <DropMenu
          open={playNowMenuOpen}
          closeMenu={() => this.setState({ playNowMenuOpen: false })}
          fixed={this.props.fixed}
          items={presentMenuItems}
        />
        <DropMenu
          open={playNextMenuOpen}
          closeMenu={() => this.setState({ playNextMenuOpen: false })}
          fixed={this.props.fixed}
          items={presentMenuItems}
        />
        {/* This modal is opened by the Album page */}
        {modals[SAVE_ALBUM_MODAL_KEY] &&
          <SaveAlbum
            title={selectedImages.length !== 0 ? 'Save images as album' : 'Save album'}
            onSubmit={this.onSave}
            closeModal={() => closeModal(SAVE_ALBUM_MODAL_KEY)}
            initialValues={initialFormValues}
            canSave={canSave && selectedImages.length === 0}
            addingToPlaylist={addingToPlaylist}
            lists={this.props.lists}
            albumLists={this.props.albumLists}
            defaultSort={query.structure.sort.value + (query.structure.sort.operator ? `-${query.structure.sort.operator}` : '')}
            presentTypeSelected={this.props.presentTypeSelected}
          />
        }
        {downloadSizeSelected &&
          <DownloadImages
            size={downloadSizeSelected}
            album={album}
            count={count}
            images={images.map(image => image._id)}
            selectedImages={selectedImages}
            getImageSource={getImageSource}
            entities={this.props.entities}
            fetchAllImages={() => this.props.searchImages(query.structure, 0, this.props.trueCount)}
            onCancel={() => this.setState({ downloadSizeSelected: false })}
          />
        }
        {shareImagesMenuOpen &&
          <ImageShare
            images={selectedImages}
            canShareInternal={this.props.canShareImagesInternal}
            canShareExternal={this.props.canShareImagesExternal}
            onCancel={() => this.setState({ shareImagesMenuOpen: false })}
          />
        }
        {addImagesMenuOpen &&
          <AddImagesToAlbum
            images={selectedImages}
            onCancel={() => this.setState({ addImagesMenuOpen: false })}
          />
        }
      </span>
    )
  }
}

const mapStateToProps = ({ playlists, entities, form, search, auth }, { album, location, canEdit }) => {
  return {
    entities,
    me: auth.user,
    presentTypeSelected:
      (form.present && form.present.values && form.present.values.presentation_type) ||
      (form.saveAlbum && form.saveAlbum.values && form.saveAlbum.values.presentation_type),
    shouldSearchImages: structure => {
      const hash = getHash(structure)
      return !search[hash] || !search[hash].data
    },
    restrictDownload: !userHasPerm(auth.user, 'no_download_restriction'),
    canEditPlaylists: userHasPerm(auth.user, 'create_channels'),
    canReorder: canEdit && !location.query.path,
    canRemove: canEdit && !location.query.path,
    fetchChannels: () => picassoFetch(auth, 'list_channels')
      .then(data => data.channels.filter(c => parseInt(c.owner_id) === auth.user.id)),
    sendToPlayNext: (channelID, body) => picassoFetch(auth, 'add_album', {
      channel_id: channelID,
      ...body
    }),
    sendToPlayNow: (channelID, body) => picassoFetch(auth, 'play_album', {
      channel_id: channelID,
      ...body
    })
  }
}

const mapDispatchToProps = {
  saveAlbum,
  deleteAlbum,
  unshareAlbum,
  openModal,
  closeModal,
  excludeImages,
  enableImageReorder,
  fetchPlaylists,
  saveList,
  addAlbumToList,
  removeAlbumFromList,
  fixAlbum,
  unfixAlbum,
  showBanner,
  presentChannel: id => dispatch => dispatch(push(`/present/${id}`)),
  goHome: id => dispatch => dispatch(push('/'))
}

export default connect(mapStateToProps, mapDispatchToProps)(AlbumMenu)



// WEBPACK FOOTER //
// ./src/components/album/AlbumMenu.js